import { useEffect, useMemo, useState } from 'react';
import { Global } from '@emotion/react';
import { Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import AxisConfigurationModalConfigurationControls from 'components/molecules/PackArchitecture/AxisConfigurationModalConfigurationControls';
import AxisConfigurationModalFooter from 'components/molecules/PackArchitecture/AxisConfigurationModalFooter';
import AxisConfigurationModalPreview from 'components/molecules/PackArchitecture/AxisConfigurationModalPreview';
import {
    getConfigurationTitle,
    getOtherGroupLabel,
} from 'components/molecules/PackArchitecture/axisConfigurationModal.constants';
import {
    AXIS_CONFIGURATION_MODAL_CLASS,
    axisConfigurationModalGlobalStyles,
} from 'components/molecules/PackArchitecture/AxisConfigurationModal.styles';
import {
    type AxisConfiguration,
    buildAxisPreview,
    clampAxisConfigurationToLoadedCount,
    configsAreEqual,
    createDefaultAxisConfiguration,
} from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import { useAxisConfigurationPreview } from 'components/molecules/PackArchitecture/useAxisConfigurationPreview';
import { getAxisConfigurationDefaultsFromMeta } from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';

export type { AxisConfiguration } from 'components/molecules/PackArchitecture/axisConfiguration.utils';

type AxisConfigurationModalProps = {
    open: boolean;
    dimensionLabel: string;
    controlsMeta?: AxisGroupByControlsMeta | null;
    initialConfig?: AxisConfiguration;
    chartRequest?: PricePackArchitectureRequest | null;
    xAxis?: string;
    apiTotalCount?: number;
    onClose: () => void;
    onApply?: (config: AxisConfiguration) => void;
};

const AxisConfigurationModal = ({
    open,
    dimensionLabel,
    controlsMeta,
    initialConfig,
    chartRequest = null,
    xAxis = '',
    apiTotalCount = 0,
    onClose,
    onApply,
}: AxisConfigurationModalProps) => {
    const modalTotalCount = apiTotalCount;
    const configurationDefaults = useMemo(
        () => getAxisConfigurationDefaultsFromMeta(controlsMeta),
        [controlsMeta],
    );

    const defaultConfig = useMemo(
        () => createDefaultAxisConfiguration(modalTotalCount, configurationDefaults),
        [configurationDefaults, modalTotalCount],
    );

    const appliedChartConfig = useMemo(() => {
        if (initialConfig) {
            return clampAxisConfigurationToLoadedCount(initialConfig, modalTotalCount);
        }
        return defaultConfig;
    }, [defaultConfig, initialConfig, modalTotalCount]);

    const [config, setConfig] = useState<AxisConfiguration>(appliedChartConfig);
    const [baselineConfig, setBaselineConfig] = useState<AxisConfiguration>(appliedChartConfig);
    const [pauseShowTopPreview, setPauseShowTopPreview] = useState(false);

    const showOwnEntityFirstOption = Boolean(controlsMeta?.ownDimensionSort);

    const { previewItems, isLoading: isPreviewLoading, showTopStats } = useAxisConfigurationPreview({
        open,
        chartRequest: chartRequest ?? null,
        xAxis,
        previewConfig: config,
        pauseFetch: pauseShowTopPreview,
    });

    useEffect(() => {
        if (open) {
            setConfig(appliedChartConfig);
            setBaselineConfig(appliedChartConfig);
            setPauseShowTopPreview(false);
        }
    }, [open, appliedChartConfig]);

    const hasApplyChanges = useMemo(
        () => !configsAreEqual(config, baselineConfig),
        [baselineConfig, config],
    );

    const hasResetChanges = useMemo(
        () => !configsAreEqual(config, defaultConfig),
        [config, defaultConfig],
    );

    const previewResult = useMemo(
        () =>
            buildAxisPreview(previewItems, config, getOtherGroupLabel(dimensionLabel), {
                preserveApiOrder: true,
                showTopStats,
            }),
        [config, dimensionLabel, previewItems, showTopStats],
    );

    const updateConfig = (patch: Partial<AxisConfiguration>) => {
        setConfig((current) => ({ ...current, ...patch }));
    };

    const handleReset = () => {
        setConfig(defaultConfig);
    };

    const handleCancel = () => {
        setConfig(baselineConfig);
        onClose();
    };

    const handleApply = () => {
        setBaselineConfig(config);
        onApply?.(config);
        onClose();
    };

    return (
        <>
            <Global styles={axisConfigurationModalGlobalStyles} />
            <Modal
                title={getConfigurationTitle(dimensionLabel)}
                open={open}
                onCancel={handleCancel}
                closeIcon={<CloseOutlined className="text-sm text-toolbarLabelGrey" />}
                width={720}
                footer={
                    <AxisConfigurationModalFooter
                        hasApplyChanges={hasApplyChanges}
                        hasResetChanges={hasResetChanges}
                        onReset={handleReset}
                        onCancel={handleCancel}
                        onApply={handleApply}
                    />
                }
                destroyOnClose
                centered
                wrapClassName={AXIS_CONFIGURATION_MODAL_CLASS}
            >
                <div className="flex h-full min-h-0">
                    {controlsMeta ? (
                        <AxisConfigurationModalConfigurationControls
                            config={config}
                            controlsMeta={controlsMeta}
                            totalCount={modalTotalCount}
                            valueSalesPercent={previewResult.valueSalesPercent}
                            volumeSalesPercent={previewResult.volumeSalesPercent}
                            isPreviewLoading={isPreviewLoading && !pauseShowTopPreview}
                            onChange={updateConfig}
                            onShowTopPreviewPauseChange={setPauseShowTopPreview}
                        />
                    ) : null}
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-axisModalBg pl-6 pt-4">
                        <AxisConfigurationModalPreview
                            config={config}
                            ownDimensionSortLabel={controlsMeta?.ownDimensionSort?.label}
                            previewResult={previewResult}
                            displayTotalCount={modalTotalCount}
                            showOwnEntityFirstOption={showOwnEntityFirstOption}
                            isLoading={isPreviewLoading && !pauseShowTopPreview}
                            onChange={updateConfig}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default AxisConfigurationModal;
